/**
 * Environment-aware GraphQL endpoint configuration
 * 
 * Best practices:
 * - Uses relative URLs for same-origin deployments (recommended)
 * - Supports explicit URLs for cross-origin deployments
 * - Detects current origin at runtime for flexibility
 * - Falls back to relative URLs when no explicit config is provided
 */

/**
 * Get the GraphQL endpoint URL based on environment and runtime context
 * 
 * Priority:
 * 1. Always prefer relative URLs when running in browser (best practice for same-origin)
 * 2. Only use explicit URLs for true cross-origin scenarios (when explicitly needed)
 * 3. Fallback to relative URL (assumes same-origin deployment)
 * 
 * This ensures that even if build-time config has a different URL, runtime detection
 * will use relative URLs when client and server are on the same origin.
 * 
 * @returns GraphQL endpoint URL
 */
export const getGraphQLEndpoint = (): string => {
  // If we're in the browser, always check current origin first
  // This ensures we use relative URLs for same-origin deployments regardless of build-time config
  if (typeof window !== 'undefined') {
    const currentOrigin = window.location.origin;
    
    // Check for explicit environment variables (from build-time)
    const explicitGraphQLUrl = import.meta.env['VITE_GRAPHQL_URL'] || import.meta.env['VITE_API_URL'];
    
    if (explicitGraphQLUrl) {
      const url = explicitGraphQLUrl.trim();
      
      try {
        const explicitUrlObj = new URL(url);
        const explicitOrigin = explicitUrlObj.origin;
        
        // If the explicit URL is on a different origin than where we're actually running,
        // this indicates a misconfiguration (build-time URL doesn't match runtime origin)
        // For same-origin deployments, always use relative URLs regardless of build-time config
        if (explicitOrigin !== currentOrigin) {
          // Log a warning to help identify misconfiguration
          if (import.meta.env.PROD) {
            console.warn(
              `[GraphQL Config] Build-time URL (${explicitOrigin}) differs from current origin (${currentOrigin}). ` +
              `Using relative URL '/graphql' for same-origin deployment. ` +
              `To fix: Rebuild client without VITE_API_URL/VITE_GRAPHQL_URL or set it to match deployment origin.`
            );
          }
          
          // Always use relative URL for same-origin deployments, even if build-time config is wrong
          return '/graphql';
        }
        
        // Same origin detected - use relative URL (best practice)
        // This avoids CSP issues and is the recommended approach
        return '/graphql';
      } catch {
        // If URL parsing fails, fall back to relative URL for safety
        console.warn('[GraphQL Config] Failed to parse explicit URL, using relative URL:', url);
        return '/graphql';
      }
    }
    
    // No explicit URL set - use relative URL (best practice for same-origin)
    return '/graphql';
  }
  
  // Server-side rendering or no window object - use relative URL
  // In development, use relative URL (Vite proxy handles it)
  if (import.meta.env.DEV) {
    return '/graphql';
  }
  
  // In production, use relative URL (best practice for same-origin deployments)
  // This works when client and server are served from the same domain
  return '/graphql';
};

/**
 * Check if the GraphQL endpoint is on the same origin as the current page
 * 
 * @param graphqlUrl - The GraphQL URL to check
 * @returns true if on same origin, false otherwise
 */
export const isSameOrigin = (graphqlUrl: string): boolean => {
  if (typeof window === 'undefined') {
    return false; // Server-side rendering
  }
  
  // Relative URLs are always same-origin
  if (graphqlUrl.startsWith('/') || !graphqlUrl.includes('://')) {
    return true;
  }
  
  try {
    const currentOrigin = window.location.origin;
    const graphqlOrigin = new URL(graphqlUrl).origin;
    return currentOrigin === graphqlOrigin;
  } catch {
    // If URL parsing fails, assume same origin for relative URLs
    return graphqlUrl.startsWith('/');
  }
};

/**
 * Get the current origin (useful for logging/debugging)
 * 
 * @returns Current origin or 'unknown' if not in browser context
 */
export const getCurrentOrigin = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }
  return window.location.origin;
};


